import Head from "next/head";
import { FunctionComponent } from "react";
import { useFragment, graphql } from "react-relay";

import { GQLEnumPostStatusEnum } from "@framework/__generated__/graphql/enums";
import { SearchTagsContainer_feedContentResource$key } from "@framework/__generated__/relay/SearchTagsContainer_feedContentResource.graphql";
import { useCoralStoryId } from "@framework/coral/use-coral-story-id";
import { withGracefulErrorBoundary } from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";

interface Props {
  feedContentResource: SearchTagsContainer_feedContentResource$key;
}

/** This component adds some additional meta tags to be used in the custom search engine */
const SearchTagsContainer: FunctionComponent<Props> = (props) => {
  const node = useFragment(
    /**
     * @excludeCacheKey: This is a utility fragment that includes shared fields from the parent
     * query. Decisions around cache key inclusion should be made in the main layout query or UI
     * components where the data directly affects page content.
     *
     * https://duet-docs.voxops.net/graphql/custom-directives/index.html#1-utility-fragments
     */
    graphql`
      fragment SearchTagsContainer_feedContentResource on ContentResourceInterface {
        id
        status
        chorusId
        chorusUuid
        comments {
          enabled
          mode
        }
        globalId

        ... on FeedContentResourceInterface {
          primaryCategory @excludeCacheKey {
            title
          }
          authors @excludeCacheKey {
            id
            name
          }
        }
      }
    `,
    props.feedContentResource,
  );

  const {
    id,
    primaryCategory,
    authors,
    status,
    chorusId,
    chorusUuid,
    globalId,
    comments,
  } = node;

  const coralId = useCoralStoryId({
    chorusId,
    chorusUuid,
    globalId,
    nodeId: id,
  });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- GraphQL string status vs enum
  if (status !== GQLEnumPostStatusEnum.publish) {
    return null;
  }

  return (
    <Head>
      {primaryCategory && (
        <meta name="cse-category" content={primaryCategory.title} />
      )}
      {comments.enabled && comments.mode === "standard" && coralId && (
        <meta name="cse-coral-id" content={coralId} />
      )}
      {authors && authors.length > 0 && (
        <meta
          name="cse-authors"
          content={authors.map((author) => author.name).join(",")}
        />
      )}
    </Head>
  );
};

export default withGracefulErrorBoundary(SearchTagsContainer, {
  context: "SearchTagsContainerErrorBoundary",
});
